/**
 * Messages Page
 *
 * Messaging interface for family members
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useMessageThreads,
  useMessagesInThread,
  useSendMessage,
  useCreateMessageThread,
  useFamilyMemberProfile,
} from '../hooks';
import { useAuth } from '@/core/hooks';
import { MessageList, MessageThread, MessageComposer } from '../components';
import { getLocalStorage, removeLocalStorage } from '@/utils/localStorage';
import type { UUID } from '@fuchsiahomecarecatalin/core/browser';

const FAMILY_MESSAGE_DRAFT_KEY = 'family:message-draft';

const standardFamilyMessages = [
  {
    subject: 'Question about today visit',
    message: 'Hello, could you please confirm how today visit went and whether there is anything the family should know?',
  },
  {
    subject: 'Request weekly summary',
    message: 'Hello, could you please send a short weekly summary of visits and any family-safe updates?',
  },
  {
    subject: 'Update contact preferences',
    message: 'Hello, I would like to update how I receive family portal notifications.',
  },
];

export const MessagesPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const familyMemberId = user?.id as UUID | null;

  // Get family member profile to get clientId
  const { data: profile } = useFamilyMemberProfile(familyMemberId);
  const clientId = profile?.clientId ?? null;

  const [showNewThread, setShowNewThread] = useState(false);
  const [newThreadSubject, setNewThreadSubject] = useState('');
  const [newThreadMessage, setNewThreadMessage] = useState('');

  const { data: threads, isLoading: threadsLoading } = useMessageThreads(familyMemberId);
  const { data: messages, isLoading: messagesLoading } = useMessagesInThread(threadId || null);
  const sendMessage = useSendMessage();
  const createThread = useCreateMessageThread();

  useEffect(() => {
    const draft = getLocalStorage<{ subject?: string; message?: string } | null>(FAMILY_MESSAGE_DRAFT_KEY, null);
    if (!draft) return;

    setNewThreadSubject(draft.subject || '');
    setNewThreadMessage(draft.message || '');
    setShowNewThread(true);
    removeLocalStorage(FAMILY_MESSAGE_DRAFT_KEY);
  }, []);

  const useMessageTemplate = (template: typeof standardFamilyMessages[number]) => {
    setNewThreadSubject(template.subject);
    setNewThreadMessage(template.message);
    setShowNewThread(true);
  };

  const handleSendMessage = (messageText: string) => {
    if (!threadId) return;
    sendMessage.mutate({ threadId, messageText });
  };

  const handleCreateThread = () => {
    if (!newThreadSubject || !newThreadMessage) {
      alert('Please fill in all fields');
      return;
    }

    if (!familyMemberId || !clientId) {
      alert('Unable to create message. Please try refreshing the page.');
      return;
    }

    createThread.mutate(
      {
        familyMemberId,
        clientId,
        subject: newThreadSubject,
        initialMessage: newThreadMessage,
      },
      {
        onSuccess: (thread) => {
          setShowNewThread(false);
          setNewThreadSubject('');
          setNewThreadMessage('');
          navigate(`/family-portal/messages/${thread.id}`);
        },
        onError: (error) => {
          alert('Failed to create message thread. Please try again.');
          console.error(error);
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="mt-1 text-gray-600">Communicate with your care team</p>
        </div>
        <button
          onClick={() => setShowNewThread(!showNewThread)}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          {showNewThread ? 'Cancel' : 'New Message'}
        </button>
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50 p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-blue-950">Standard message templates</h2>
        <p className="mt-1 text-sm text-blue-800">
          Use one clear template to reduce repeated phone calls and help the care team answer faster.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {standardFamilyMessages.map((template) => (
            <button
              key={template.subject}
              type="button"
              onClick={() => useMessageTemplate(template)}
              className="rounded-lg border border-blue-200 bg-white p-4 text-left hover:border-blue-400 hover:bg-blue-50"
            >
              <span className="block text-sm font-semibold text-gray-950">{template.subject}</span>
              <span className="mt-2 block text-xs leading-5 text-gray-600">{template.message}</span>
            </button>
          ))}
        </div>
      </div>

      {/* New Thread Form */}
      {showNewThread && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">New Message</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={newThreadSubject}
                onChange={(e) => setNewThreadSubject(e.target.value)}
                placeholder="What is this about?"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={newThreadMessage}
                onChange={(e) => setNewThreadMessage(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={handleCreateThread}
              disabled={createThread.isPending}
              className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {createThread.isPending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </div>
      )}

      {/* Layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Thread List */}
        <div className="lg:col-span-1">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Conversations</h2>
            <MessageList threads={threads || []} loading={threadsLoading} />
          </div>
        </div>

        {/* Message Thread */}
        <div className="lg:col-span-2">
          {threadId ? (
            <div className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 p-4">
                <h2 className="text-base font-semibold text-gray-900">
                  {threads?.find((t) => t.id === threadId)?.subject || 'Conversation'}
                </h2>
              </div>
              <div className="flex-1 overflow-y-auto" style={{ maxHeight: '500px' }}>
                <MessageThread messages={messages || []} loading={messagesLoading} />
              </div>
              <MessageComposer
                onSend={handleSendMessage}
                disabled={sendMessage.isPending}
                placeholder="Type your message..."
              />
            </div>
          ) : (
            <div className="flex h-96 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500">
              <div className="text-center">
                <p className="text-3xl mb-2">💬</p>
                <p>Select a conversation or start a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
